import { projectsApi } from '@/api/projects.api';
import { useToolSystem } from '@/context/ToolSystemContext';
import type { ProjectListItem, SortField, SortOrder } from '@/types/project.types';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const DEFAULT_PAGE_SIZE = 8;
const DEFAULT_SORT: SortField = 'created_at';
const DEFAULT_ORDER: SortOrder = 'desc';

export const useProjects = () => {
  const { toolSystem } = useToolSystem();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('size') ?? DEFAULT_PAGE_SIZE);
  const sort = (searchParams.get('sort') as SortField | null) ?? DEFAULT_SORT;
  const order = (searchParams.get('order') as SortOrder | null) ?? DEFAULT_ORDER;
  const ownerFilter = searchParams.get('owner_id');

  const setPage = useCallback(
    (next: number) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('page', String(next));
        return p;
      });
    },
    [setSearchParams]
  );

  const setSort = useCallback(
    (nextSort: SortField, nextOrder: SortOrder) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('sort', nextSort);
        p.set('order', nextOrder);
        p.set('page', '1');
        return p;
      });
    },
    [setSearchParams]
  );

  const setOwnerFilter = useCallback(
    (ownerId: string | null) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (ownerId == null || ownerId === '') {
          p.delete('owner_id');
        } else {
          p.set('owner_id', ownerId);
        }
        p.set('page', '1');
        return p;
      });
    },
    [setSearchParams]
  );

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [data, total] = await Promise.all([
        projectsApi.getAllMainPage({
          page,
          size: pageSize,
          sort,
          order,
          // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
          owner_id: ownerFilter,
          // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
          tool_system: toolSystem,
        }),
        // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
        projectsApi.getCount({ owner_id: ownerFilter, tool_system: toolSystem }),
      ]);
      setProjects(data);
      setTotalCount(total);
    } catch (_err) {
      const message = 'Failed to load projects';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sort, order, ownerFilter, toolSystem]);

  // Refetch whenever page / sort / order / owner filter changes.
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const deleteProject = useCallback(
    async (projectId: string) => {
      try {
        await projectsApi.delete(projectId);
        toast.success('Project deleted');
        await fetchProjects();
      } catch (err) {
        toast.error('Failed to delete project');
        throw err;
      }
    },
    [fetchProjects]
  );

  const bulkDeleteProjects = useCallback(
    async (projectIds: string[]) => {
      try {
        const result = await projectsApi.bulkDelete(projectIds);

        if (result.deleted.length > 0) {
          toast.success(`Successfully deleted ${result.deleted.length} project(s)`);
        }

        if (result.failed.length > 0) {
          result.failed.forEach((failure) => {
            toast.error(`Failed to delete project: ${failure.error}`);
          });
        }

        await fetchProjects();
        return result;
      } catch (err) {
        toast.error('Failed to delete projects');
        throw err;
      }
    },
    [fetchProjects]
  );

  const hasNextPage = pageSize > 0 && projects.length === pageSize;
  const hasPrevPage = page > 1;
  const totalPages = totalCount != null && pageSize > 0 ? Math.ceil(totalCount / pageSize) : null;

  return {
    projects,
    isLoading,
    error,
    refetch: fetchProjects,
    deleteProject,
    bulkDeleteProjects,
    page,
    pageSize,
    setPage,
    hasNextPage,
    hasPrevPage,
    totalPages,
    totalCount,
    sort,
    order,
    setSort,
    ownerFilter,
    setOwnerFilter,
  };
};
